import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Download, ChevronUp, ChevronDown } from 'lucide-react';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell,
  TableFooter
} from '../../components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

interface ShirtSizeStats {
  shirt_size: string;
  count: number;
}

type SortDirection = 'asc' | 'desc';

export default function ShirtSummaryReport() {
  const [shirtSizeStats, setShirtSizeStats] = useState<ShirtSizeStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);
  const [sortByCount, setSortByCount] = useState(false);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  async function fetchShirtSizeStats() {
    setLoading(true);
    setError('');
    setReportGenerated(true);
    setShirtSizeStats([]);

    try {
      const { data, error: fetchError } = await supabase
        .rpc('get_shirt_size_summary');

      if (fetchError) {
        throw fetchError;
      }
      
      if (data && data.length > 0) {
        setShirtSizeStats(data);
      } else {
        setShirtSizeStats([]);
      }
    } catch (err: any) {
      console.error('Error fetching shirt size statistics:', err);
      setError(err.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }

  const handleSort = () => {
    setSortByCount(!sortByCount);
  };

  const handleSortDirection = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };

  const getSortedStats = () => {
    if (shirtSizeStats.length === 0) return [];
    
    return [...shirtSizeStats].sort((a, b) => {
      if (sortByCount) {
        return sortDirection === 'asc' 
          ? a.count - b.count 
          : b.count - a.count;
      } else {
        // Sort by shirt size name
        if (a.shirt_size === null) return sortDirection === 'asc' ? 1 : -1;
        if (b.shirt_size === null) return sortDirection === 'asc' ? -1 : 1;
        
        return sortDirection === 'asc'
          ? a.shirt_size.localeCompare(b.shirt_size)
          : b.shirt_size.localeCompare(a.shirt_size);
      }
    });
  };

  const getTotalCount = () => {
    return shirtSizeStats.reduce((sum, stat) => sum + Number(stat.count), 0);
  };

  const exportCSV = () => {
    if (shirtSizeStats.length === 0) return;
    
    const headers = ['Shirt Size', 'Count', 'Percentage'];
    const totalCount = getTotalCount();
    const sortedData = getSortedStats();

    const csvContent = [
      headers.join(','),
      ...sortedData.map(stat => [
        `"${stat.shirt_size}"`,
        stat.count,
        `"${((Number(stat.count) / totalCount) * 100).toFixed(1)}%"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `shirt_size_summary_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const SortIcon = () => {
    return sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Shirt Size Summary</CardTitle>
        <CardDescription>Summary of registered players by shirt size.</CardDescription>
        <div className="flex justify-end space-x-2">
          <Button onClick={fetchShirtSizeStats} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>
          {reportGenerated && shirtSizeStats.length > 0 && (
            <Button variant="outline" onClick={exportCSV} disabled={shirtSizeStats.length === 0}>
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading && <p>Loading...</p>}
        {error && <p className="text-red-500">Error: {error}</p>}
        {reportGenerated && !loading && shirtSizeStats.length === 0 && (
          <p>No player data found.</p>
        )}
        {reportGenerated && !loading && shirtSizeStats.length > 0 && (
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer" 
                    onClick={handleSortDirection}>
                    Shirt Size <SortIcon />
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer text-right" 
                    onClick={handleSort}>
                    Count {sortByCount && <SortIcon />}
                  </TableHead>
                  <TableHead className="text-right">Percentage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getSortedStats().map((stat) => {
                  const percentage = ((Number(stat.count) / getTotalCount()) * 100).toFixed(1);
                  return (
                    <TableRow key={stat.shirt_size}>
                      <TableCell className="font-medium">{stat.shirt_size}</TableCell>
                      <TableCell className="text-right">{stat.count}</TableCell>
                      <TableCell className="text-right">{percentage}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="font-medium">Total</TableCell>
                  <TableCell className="text-right">{getTotalCount()}</TableCell>
                  <TableCell className="text-right">100%</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
